import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useLocation, useNavigate } from "react-router-dom";
import PaywallButton from "../Mui/PaywallButton";
import "../../../stylesheets/chat_page.css";
import { isMobileBrowser } from "../../utils/user_agent";
import {
  getInAppModalConfig,
  inAppNotificationActionState,
  IN_APP_MODAL_BADGE_THEME_OPTIONS,
} from "./inAppModalUtils";
import { trackFrontendEvent } from "../../utils/tracking";

export default function InAppNotificationModal({
  notification,
  onClose,
  sidebarOffset = 0,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = isMobileBrowser();
  const modalRef = useRef(null);
  const textScrollRef = useRef(null);
  const [opacity, setOpacity] = useState(0);
  const [showTextFade, setShowTextFade] = useState(false);

  useEffect(() => {
    setOpacity(1);
  }, []);

  const modalConfig = useMemo(
    () => getInAppModalConfig(notification?.metadata || {}),
    [notification?.metadata]
  );
  const actionState = inAppNotificationActionState({
    actionUrl: notification?.actionUrl,
    buttonLabel: modalConfig?.buttonLabel,
    currentPageButtonLabel: modalConfig?.currentPageButtonLabel,
    currentPathname: location.pathname,
    currentSearch: location.search,
    currentHash: location.hash,
  });
  const { actionUrl, primaryLabel, staysOnCurrentPage } = actionState;
  const badgeTheme = IN_APP_MODAL_BADGE_THEME_OPTIONS.find(
    (theme) => theme.value === modalConfig?.badgeTheme
  ) || IN_APP_MODAL_BADGE_THEME_OPTIONS[0];
  const darkBadgeTheme = {
    color: badgeTheme.darkColor || badgeTheme.color,
    backgroundColor: badgeTheme.darkBackgroundColor || badgeTheme.backgroundColor,
    borderColor: badgeTheme.darkBorderColor || badgeTheme.borderColor,
  };
  const modalMessage = useMemo(
    () => `${notification?.text || ""}${notification?.body ? `\n\n${notification.body}` : ""}`,
    [notification?.body, notification?.text]
  );
  const examplePrompt = (modalConfig?.examplePrompt || "").trim();
  const footnote = (modalConfig?.footnote || "").trim();
  const titleId = `in-app-notification-title-${notification?.id || "active"}`;

  const productUpdateTrackingMetadata = useMemo(() => ({
    notification_id: notification?.id,
    title: notification?.title,
    kind: notification?.kind,
    audience: notification?.metadata?.audience,
    badge_text: modalConfig?.badgeLabel,
    badge_theme: modalConfig?.badgeTheme,
    cta_label: primaryLabel,
    action_url_present: Boolean(actionUrl),
    action_mode: staysOnCurrentPage ? "close_in_place" : "navigate",
  }), [
    actionUrl,
    modalConfig?.badgeLabel,
    modalConfig?.badgeTheme,
    notification?.id,
    notification?.kind,
    notification?.metadata?.audience,
    notification?.title,
    primaryLabel,
    staysOnCurrentPage,
  ]);

  useEffect(() => {
    const updateTextFade = () => {
      const node = textScrollRef.current;
      if (!node) return;
      setShowTextFade(node.scrollHeight - node.clientHeight - node.scrollTop > 4);
    };

    updateTextFade();
    const frameId = window.requestAnimationFrame(updateTextFade);
    window.addEventListener("resize", updateTextFade);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updateTextFade);
    };
  }, [modalMessage]);

  useEffect(() => {
    const modalNode = modalRef.current;
    if (!modalNode) return undefined;

    modalNode.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (modalConfig?.design === "modal_2") {
          trackFrontendEvent("product_update_modal_dismissed", productUpdateTrackingMetadata);
        }
        onClose?.();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = modalNode.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    modalNode.addEventListener("keydown", handleKeyDown);
    return () => modalNode.removeEventListener("keydown", handleKeyDown);
  }, [modalConfig?.design, onClose, productUpdateTrackingMetadata]);

  useEffect(() => {
    if (modalConfig?.design !== "modal_2") return;
    trackFrontendEvent("product_update_modal_viewed", productUpdateTrackingMetadata);
  }, [modalConfig?.design, productUpdateTrackingMetadata]);

  const handleDismiss = () => {
    if (modalConfig?.design === "modal_2") {
      trackFrontendEvent("product_update_modal_dismissed", productUpdateTrackingMetadata);
    }
    onClose?.();
  };

  const handlePrimaryAction = () => {
    if (modalConfig?.design === "modal_2") {
      trackFrontendEvent("product_update_modal_cta_clicked", productUpdateTrackingMetadata);
    }
    onClose?.();
    if (!actionUrl || staysOnCurrentPage) {
      if (modalConfig?.design === "modal_2") {
        trackFrontendEvent("product_update_modal_action_completed", productUpdateTrackingMetadata);
      }
      return;
    }

    if (/^https?:\/\//i.test(actionUrl)) {
      window.open(actionUrl, "_blank", "noopener,noreferrer");
      if (modalConfig?.design === "modal_2") {
        trackFrontendEvent("product_update_modal_action_completed", productUpdateTrackingMetadata);
      }
      return;
    }

    if (/^(mailto:|tel:|sms:)/i.test(actionUrl)) {
      window.location.href = actionUrl;
      if (modalConfig?.design === "modal_2") {
        trackFrontendEvent("product_update_modal_action_completed", productUpdateTrackingMetadata);
      }
      return;
    }

    navigate(actionUrl);
    if (modalConfig?.design === "modal_2") {
      trackFrontendEvent("product_update_modal_action_completed", productUpdateTrackingMetadata);
    }
  };

  if (modalConfig?.design === "modal_2") {
    return (
      <Box
        className="modal modal-paywall"
        role="presentation"
        sx={{
          opacity,
          transition: "opacity 0.35s ease",
          backgroundColor: "rgba(2, 6, 23, 0.72)",
          zIndex: 1600,
          px: "max(12px, env(safe-area-inset-left))",
          py: "max(12px, env(safe-area-inset-top))",
        }}
      >
        <Box
          ref={modalRef}
          className="base-text"
          data-in-app-notification-dialog
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          sx={{
            position: "relative",
            width: "520px",
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100dvh - 20px)",
            boxSizing: "border-box",
            overflow: "hidden",
            borderRadius: "18px",
            border: "1px solid rgba(96, 165, 250, 0.24)",
            background: "radial-gradient(circle at 18% 0%, rgba(59,130,246,0.26), transparent 34%), linear-gradient(145deg, #07111F 0%, #0D1728 52%, #111827 100%)",
            boxShadow: "0 30px 90px rgba(0, 0, 0, 0.48)",
            color: "#F8FAFC",
            p: { xs: "24px 20px 22px", sm: "30px 32px 28px" },
            display: "flex",
            flexDirection: "column",
            outline: "none",
          }}
        >
          <IconButton
            aria-label="Close notification"
            onClick={handleDismiss}
            sx={{
              position: "absolute",
              top: { xs: 14, sm: 18 },
              right: { xs: 14, sm: 18 },
              width: 36,
              height: 36,
              color: "rgba(226,232,240,0.82)",
              border: "1px solid rgba(148,163,184,0.20)",
              backgroundColor: "rgba(15,23,42,0.42)",
              "&:hover": {
                color: "#FFFFFF",
                backgroundColor: "rgba(30,41,59,0.82)",
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 19 }} />
          </IconButton>

          <Box
            sx={{
              width: "fit-content",
              mt: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: "999px",
              fontWeight: 700,
              fontSize: "11px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: darkBadgeTheme.color,
              backgroundColor: darkBadgeTheme.backgroundColor,
              border: `1px solid ${darkBadgeTheme.borderColor}`,
              boxShadow: `0 0 24px ${darkBadgeTheme.backgroundColor}`,
            }}
          >
            {modalConfig?.badgeLabel || "New Feature"}
          </Box>

          <Box
            id={titleId}
            sx={{
              mt: 1.5,
              fontWeight: 760,
              fontSize: { xs: "25px", sm: "29px" },
              lineHeight: "118%",
              color: "#F8FAFC",
              letterSpacing: 0,
            }}
          >
            {notification?.title || "Notification"}
          </Box>

          <Box sx={{ position: "relative", mt: 2, minHeight: 0 }}>
            <Box
              ref={textScrollRef}
              data-in-app-notification-message
              onScroll={(event) => {
                const node = event.currentTarget;
                setShowTextFade(node.scrollHeight - node.clientHeight - node.scrollTop > 4);
              }}
              sx={{
                maxHeight: examplePrompt ? "min(40vh, 360px)" : "min(52vh, 470px)",
                minHeight: 0,
                overflowY: "auto",
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
                pr: 1,
                whiteSpace: "pre-wrap",
                fontWeight: 500,
                fontSize: { xs: "15px", sm: "15.5px" },
                lineHeight: "168%",
                color: "rgba(226,232,240,0.88)",
                textAlign: "left",
              }}
            >
              {modalMessage}
            </Box>

            {showTextFade && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 54,
                  pointerEvents: "none",
                  background: "linear-gradient(180deg, rgba(13,23,40,0) 0%, rgba(13,23,40,0.96) 90%)",
                }}
              />
            )}
          </Box>

          {examplePrompt && (
            <Box
              sx={{
                mt: 2.25,
                p: 1.6,
                borderRadius: "14px",
                border: "1px solid rgba(148,163,184,0.18)",
                background: "rgba(15,23,42,0.58)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              <Box sx={{ color: "#93C5FD", fontSize: "13px", fontWeight: 700, mb: 0.75 }}>
                Try asking:
              </Box>
              <Box sx={{ color: "#F8FAFC", fontSize: "15px", lineHeight: "158%", fontWeight: 560 }}>
                {examplePrompt}
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1, mt: 2.5, flexWrap: "wrap" }}>
            <Button
              data-in-app-notification-primary-action
              variant="contained"
              onClick={handlePrimaryAction}
              sx={{
                flex: "1 1 190px",
                minHeight: 42,
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 700,
                background: "linear-gradient(135deg, #2F7DF4 0%, #1D4ED8 100%)",
                boxShadow: "0 14px 32px rgba(37,99,235,0.30)",
                "&:hover": {
                  background: "linear-gradient(135deg, #4A8EF6 0%, #2563EB 100%)",
                },
              }}
            >
              {primaryLabel}
            </Button>
            {primaryLabel !== "Got it" && (
              <Button
                data-in-app-notification-secondary-action
                variant="text"
                onClick={handleDismiss}
                sx={{
                  flex: "0 1 auto",
                  minHeight: 42,
                  px: 2,
                  borderRadius: "999px",
                  textTransform: "none",
                  color: "rgba(226,232,240,0.78)",
                  fontWeight: 650,
                  "&:hover": {
                    color: "#FFFFFF",
                    backgroundColor: "rgba(148,163,184,0.10)",
                  },
                }}
              >
                Maybe later
              </Button>
            )}
          </Box>

          {footnote && (
            <Box
              data-in-app-notification-footnote
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop: "1px solid rgba(148,163,184,0.16)",
                fontSize: { xs: "11.5px", sm: "12px" },
                lineHeight: "155%",
                fontWeight: 500,
                color: "rgba(203,213,225,0.68)",
              }}
            >
              {footnote}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <div
      className="modal modal-paywall"
      role="presentation"
      style={{
        opacity,
        transition: "opacity 0.35s ease",
        backgroundColor: "rgba(15, 23, 42, 0.28)",
        zIndex: 1600,
      }}
    >
      <div
        ref={modalRef}
        className="paywall-modal-content"
        data-in-app-notification-dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{
          marginLeft: !isMobile ? `${sidebarOffset / 2}px` : "unset",
          background: "linear-gradient(180deg, #DBE1F5 0%, #FFFFFF 100%)",
          width: "420px",
          maxWidth: "88%",
          maxHeight: "calc(100dvh - 32px)",
          boxSizing: "border-box",
          padding: "30px 0",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          outline: "none",
          boxShadow: "0 28px 90px rgba(15, 23, 42, 0.22)",
        }}
      >
        <Box
          className="base-text"
          sx={{
            fontSize: "15px",
            color: "#2971D7",
            fontWeight: 500,
            position: "absolute",
            top: "20px",
            left: "20px",
            "&:hover": { cursor: "pointer" },
          }}
          onClick={handleDismiss}
        >
          Close
        </Box>

        <Box
          className="base-text"
          id={titleId}
          sx={{
            margin: "18px auto 0 auto",
            maxWidth: "84%",
            fontWeight: 600,
            fontSize: "28px",
            lineHeight: "124%",
            color: "#111827",
          }}
        >
          {notification?.title || "Notification"}
        </Box>

        <Box
          className="base-text"
          sx={{
            width: "fit-content",
            margin: "14px auto 0 auto",
            px: 1.25,
            py: 0.5,
            borderRadius: "999px",
            fontWeight: 600,
            fontSize: "11px",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: badgeTheme.color,
            backgroundColor: badgeTheme.backgroundColor,
            border: `1px solid ${badgeTheme.borderColor}`,
          }}
        >
          {modalConfig?.badgeLabel || "New Feature"}
        </Box>

        <Box sx={{ position: "relative", width: "84%", margin: "18px auto 0 auto", minHeight: 0 }}>
          <Box
            ref={textScrollRef}
            className="base-text"
            data-in-app-notification-message
            onScroll={(event) => {
              const node = event.currentTarget;
              setShowTextFade(node.scrollHeight - node.clientHeight - node.scrollTop > 4);
            }}
            sx={{
              maxHeight: "min(34vh, 320px)",
              minHeight: 0,
              overflowY: "auto",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
              whiteSpace: "pre-wrap",
              fontWeight: 500,
              fontSize: "15px",
              lineHeight: "165%",
              color: "#334155",
              textAlign: "left",
              px: 1,
              pr: 1.5,
            }}
          >
            {modalMessage}
          </Box>

          {showTextFade && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 46,
                pointerEvents: "none",
                background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.94) 88%)",
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            width: "260px",
            margin: "20px auto 0 auto",
            maxWidth: "84%",
            minHeight: "46px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PaywallButton
            data-in-app-notification-primary-action
            onClick={handlePrimaryAction}
            sx={{
              backgroundColor: "#3974CC",
              padding: "6px 12px",
              "&:hover": { backgroundColor: "#3974CC" },
            }}
          >
            <Box
              sx={{
                width: "100%",
                fontWeight: 500,
                fontSize: "13px",
                margin: "4px auto",
              }}
            >
              {primaryLabel}
            </Box>
          </PaywallButton>
        </Box>

        {footnote && (
          <Box
            className="base-text"
            data-in-app-notification-footnote
            sx={{
              width: "84%",
              margin: "16px auto 0 auto",
              pt: 1.4,
              borderTop: "1px solid rgba(100,116,139,0.16)",
              fontSize: "11.5px",
              lineHeight: "155%",
              fontWeight: 500,
              color: "#64748B",
              textAlign: "left",
            }}
          >
            {footnote}
          </Box>
        )}
      </div>
    </div>
  );
}
